import axios from 'axios'
import apiService from './apiService'

export default {
  // Récupérer les factures d'un contrat spécifique
  async getFacturesContrat(contratId) {
    try {
      const response = await apiService.get(`/contrats/${contratId}/factures`)
      return response
    } catch (error) {
      console.error('Erreur lors de la récupération des factures du contrat:', error)
      throw error
    }
  },
  // Récupérer toutes les factures avec filtres
  async getFactures(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString()
      const endpoint = `/factures${queryParams ? `?${queryParams}` : ''}`
      return await apiService.get(endpoint)
    } catch (error) {
      console.error('Error fetching factures:', error)
      throw error
    }
  },

  // Récupérer une facture par ID
  async getFactureById(id) {
    try {
      return await apiService.get(`/factures/${id}`)
    } catch (error) {
      console.error('Error fetching facture:', error)
      throw error
    }
  },

  // Créer une nouvelle facture
  async createFacture(factureData) {
    try {
      return await apiService.post('/factures', factureData)
    } catch (error) {
      console.error('Error creating facture:', error)
      throw error
    }
  },
  
  // Créer une nouvelle facture avec des fichiers (preuve de paiement, PDF, etc.)
  async createFactureWithFiles(formData) {
    try {
      // Utiliser axios directement pour gérer le multipart/form-data
      const headers = {
        'Content-Type': 'multipart/form-data'
      }
      
      // Ajouter le token d'authentification si disponible
      const token = localStorage.getItem('token')
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL || 'http://localhost:8000/api'}/factures`, 
        formData,
        { headers }
      )
      return response.data
    } catch (error) {
      console.error('Error creating facture with files:', error)
      // Log detailed validation errors if available
      if (error.response && error.response.data) {
        console.error('Validation errors:', error.response.data)
      }
      throw error
    }
  },

  // Mettre à jour une facture
  async updateFacture(id, factureData) {
    try {
      return await apiService.put(`/factures/${id}`, factureData)
    } catch (error) {
      console.error('Error updating facture:', error)
      throw error
    }
  },

  // Supprimer une facture
  async deleteFacture(id) {
    try {
      return await apiService.delete(`/factures/${id}`)
    } catch (error) {
      console.error('Error deleting facture:', error)
      throw error
    }
  },

  // Changer le statut d'une facture
  async updateFactureStatus(id, dataOrStatus, formData = null) {
    try {
      // Handle different parameter formats
      if (dataOrStatus instanceof FormData) {
        // If second parameter is FormData, use it directly
        const headers = {
          // Don't set Content-Type when using FormData - let the browser set it with correct boundary
        }
        
        // Log what we're sending
        console.log('Sending FormData to update invoice', id)
        for (let pair of dataOrStatus.entries()) {
          console.log('FormData field:', pair[0], pair[1] instanceof File ? 
                    `File: ${pair[1].name}, size: ${pair[1].size}, type: ${pair[1].type}` : 
                    pair[1])
        }
        
        // Ajouter le token d'authentification si disponible
        const token = localStorage.getItem('token')
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }
        
        const response = await axios.patch(
          `${process.env.VUE_APP_API_URL || 'http://localhost:8000/api'}/factures/${id}/status`, 
          dataOrStatus,
          { headers }
        )
        return response.data
      } else if (formData) {
        // Legacy support - if formData is provided as third parameter
        const headers = {
          // Don't set Content-Type when using FormData - let the browser set it with correct boundary
        }
        
        // Log what we're sending
        console.log('Sending legacy FormData to update invoice', id)
        for (let pair of formData.entries()) {
          console.log('Legacy FormData field:', pair[0], pair[1] instanceof File ? 
                    `File: ${pair[1].name}, size: ${pair[1].size}, type: ${pair[1].type}` : 
                    pair[1])
        }
        
        const token = localStorage.getItem('token')
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }
        
        const response = await axios.patch(
          `${process.env.VUE_APP_API_URL || 'http://localhost:8000/api'}/factures/${id}/status`, 
          formData,
          { headers }
        )
        return response.data
      } else {
        // Simple status update
        return await apiService.patch(`/factures/${id}/status`, { status: dataOrStatus })
      }
    } catch (error) {
      console.error('Error updating facture status:', error)
      throw error
    }
  },

  // Télécharger une facture en PDF
  async downloadFacturePdf(id) {
    try {
      const response = await fetch(`${apiService.baseURL}/factures/${id}/pdf`, {
        headers: apiService.getHeaders()
      })
      
      if (!response.ok) {
        throw new Error('PDF not found')
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `facture-${id}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading facture PDF:', error)
      throw error
    }
  },

  // Imprimer une facture
  async printFacture(id) {
    try {
      const response = await fetch(`${apiService.baseURL}/factures/${id}/print`, {
        headers: apiService.getHeaders()
      })
      
      if (!response.ok) {
        throw new Error('Print failed')
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url)
      printWindow.onload = () => {
        printWindow.print()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error printing facture:', error)
      throw error
    }
  },

  // Obtenir les factures d'un contrat
  async getFacturesByContrat(contratId) {
    try {
      return await apiService.get(`/contrats/${contratId}/factures`)
    } catch (error) {
      console.error('Error fetching factures by contrat:', error)
      throw error
    }
  },

  // Obtenir les statistiques des factures
  async getFactureStats() {
    try {
      return await apiService.get('/factures/stats')
    } catch (error) {
      console.error('Error fetching facture stats:', error)
      throw error
    }
  },

  // Rechercher des factures
  async searchFactures(query) {
    try {
      return await apiService.get(`/factures/search?q=${encodeURIComponent(query)}`)
    } catch (error) {
      console.error('Error searching factures:', error)
      throw error
    }
  },

  // Obtenir les factures en attente de règlement
  async getFacturesEnAttente() {
    try {
      console.log('Fetching pending invoices from API...');
      
      // Try to get invoices specifically formatted for alerts view first
      try {
        console.log('Fetching invoices formatted for alerts view...');
        const alertsResponse = await apiService.get('/factures/alertes');
        console.log('Alerts invoices API response:', alertsResponse);
        
        if (alertsResponse && alertsResponse.data && 
            (Array.isArray(alertsResponse.data) || 
            (alertsResponse.data.data && Array.isArray(alertsResponse.data.data)))) {
          // If we get a valid response, return it directly
          return alertsResponse;
        }
      } catch (alertsError) {
        console.error('Error fetching alerts invoices:', alertsError);
      }
      
      // Fallback to standard endpoints if the alerts endpoint fails
      try {
        // First try with the standard endpoint
        const response = await apiService.get('/factures/en-attente')
        console.log('Pending invoices API response:', response);
        
        // Filter to ensure only unpaid invoices are returned
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            response.data = response.data.filter(invoice => 
              invoice.status === 'pending' || invoice.status === 'late' || invoice.status === 'NR');
          } else if (response.data.data && Array.isArray(response.data.data)) {
            response.data.data = response.data.data.filter(invoice => 
              invoice.status === 'pending' || invoice.status === 'late' || invoice.status === 'NR');
          }
        }
        
        return response;
      } catch (firstError) {
        console.error('First attempt failed:', firstError);
        
        // If that fails, try the alternative endpoint format
        console.log('Trying alternative endpoint format...');
        const alternativeResponse = await apiService.get('/factures?status=pending')
        console.log('Alternative pending invoices API response:', alternativeResponse);
        
        // Filter to ensure only unpaid invoices are returned
        if (alternativeResponse && alternativeResponse.data) {
          if (Array.isArray(alternativeResponse.data)) {
            alternativeResponse.data = alternativeResponse.data.filter(invoice => 
              invoice.status === 'pending' || invoice.status === 'late' || invoice.status === 'NR');
          } else if (alternativeResponse.data.data && Array.isArray(alternativeResponse.data.data)) {
            alternativeResponse.data.data = alternativeResponse.data.data.filter(invoice => 
              invoice.status === 'pending' || invoice.status === 'late' || invoice.status === 'NR');
          }
        }
        
        return alternativeResponse;
      }
    } catch (error) {
      console.error('Error fetching factures en attente:', error)
      
      // Fallback to empty response for development
      console.log('Returning empty response as fallback');
      return { data: [] };
    }
  },
  
  // Obtenir les factures spécifiquement pour la vue des alertes (avec dates d'échéance)
  async getFacturesForAlerts() {
    try {
      const response = await apiService.get('/factures/alertes');
      console.log('Alerts invoices response:', response);
      return response;
    } catch (error) {
      console.error('Error fetching factures for alerts:', error);
      throw error;
    }
  },

  // Obtenir les factures réglées
  async getFacturesReglees() {
    try {
      return await apiService.get('/factures/reglees')
    } catch (error) {
      console.error('Error fetching factures reglees:', error)
      throw error
    }
  }
} 